/**
 * @file tasks.js
 * @description Task Management System utilities and Firestore service layer.
 *
 * REQUIRED ONE-TIME FIRESTORE SETUP:
 * Before creating the first task, you must manually (or via setup script) create a document at:
 *   Path: counters/taskCounter
 *   Data: { lastNumber: 99 }
 * lastNumber is initialized to 99 so that the first generated task number is 100.
 */

import { firestore } from "./config";
import { getAuth } from "firebase/auth";
import {
  doc,
  collection,
  query,
  where,
  orderBy,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  runTransaction,
  serverTimestamp,
  onSnapshot,
  writeBatch,
  deleteDoc
} from "firebase/firestore";

/**
 * Generates a unique task number sequentially using a Firestore transaction.
 * @returns {Promise<number>} The generated task number (e.g. 100, 101, 102...)
 */
export async function generateTaskNumber() {
  const counterDocRef = doc(firestore, "counters", "taskCounter");

  return await runTransaction(firestore, async (transaction) => {
    const counterDoc = await transaction.get(counterDocRef);

    let nextNumber;
    if (!counterDoc.exists()) {
      nextNumber = 100;
      transaction.set(counterDocRef, { lastNumber: 100 });
    } else {
      const currentNumber = counterDoc.data().lastNumber;
      if (typeof currentNumber !== "number") {
        throw new Error("counters/taskCounter.lastNumber is not a valid number.");
      }
      nextNumber = currentNumber + 1;
      transaction.update(counterDocRef, { lastNumber: nextNumber });
    }

    return nextNumber;
  });
}

/**
 * Creates a new task document in Firestore within a transaction.
 * @param {Object} taskData - The initial task details.
 * @returns {Promise<Object>} The created task object.
 */
export async function createTask(taskData) {
  const counterDocRef = doc(firestore, "counters", "taskCounter");
  const newTaskDocRef = doc(collection(firestore, "tasks"));
  let nextNumber;

  const { category, ...restTaskData } = taskData;
  const labels = restTaskData.labels || [];

  await runTransaction(firestore, async (transaction) => {
    const counterDoc = await transaction.get(counterDocRef);

    if (!counterDoc.exists()) {
      nextNumber = 100;
      transaction.set(counterDocRef, { lastNumber: 100 });
    } else {
      const currentNumber = counterDoc.data().lastNumber;
      if (typeof currentNumber !== "number") {
        throw new Error("counters/taskCounter.lastNumber is not a valid number.");
      }
      nextNumber = currentNumber + 1;
      transaction.update(counterDocRef, { lastNumber: nextNumber });
    }

    const finalTaskData = {
      ...restTaskData,
      labels,
      taskNumber: nextNumber,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    transaction.set(newTaskDocRef, finalTaskData);
  });

  return {
    id: newTaskDocRef.id,
    taskNumber: nextNumber,
    ...restTaskData,
    labels
  };
}

/**
 * Fetches a task document by its unique sequential taskNumber.
 * @param {number|string} taskNumber
 * @returns {Promise<Object|null>}
 */
export async function getTaskByNumber(taskNumber) {
  const tasksRef = collection(firestore, "tasks");
  const q = query(tasksRef, where("taskNumber", "==", Number(taskNumber)));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) return null;

  const docSnap = querySnapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Fetches a task document by its Firestore document ID.
 * @param {string} docId
 * @returns {Promise<Object|null>}
 */
export async function getTaskById(docId) {
  const docRef = doc(firestore, "tasks", docId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return null;

  return { id: docSnap.id, ...docSnap.data() };
}

/**
 * Updates an existing task document in Firestore and sets the updatedAt field.
 * @param {string} docId
 * @param {Object} updates
 * @returns {Promise<void>}
 */
export async function updateTask(docId, updates) {
  const docRef = doc(firestore, "tasks", docId);
  await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
}

/**
 * Lists tasks matching the specified optional filters.
 * @param {Object} [filters]
 * @returns {Promise<Object[]>}
 */
export async function listTasks(filters = {}) {
  const tasksRef = collection(firestore, "tasks");
  const constraints = [];

  if (filters.assignedTo) {
    constraints.push(where("assigneeUid", "==", filters.assignedTo));
  }
  if (filters.createdBy) {
    constraints.push(where("createdByUid", "==", filters.createdBy));
  }
  if (filters.state && Array.isArray(filters.state) && filters.state.length > 0) {
    constraints.push(where("status", "in", filters.state));
  }
  if (filters.priority) {
    constraints.push(where("priority", "==", filters.priority));
  }
  if (filters.label) {
    constraints.push(where("labels", "array-contains", filters.label));
  }

  const q = query(tasksRef, ...constraints);
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(docSnap => ({
    id: docSnap.id,
    labels: [],
    ...docSnap.data()
  }));
}

/**
 * Adds a new comment document to the comments subcollection of a specific task.
 * @param {string} taskId
 * @param {Object} commentData
 * @returns {Promise<Object>}
 */
export async function addComment(taskId, commentData) {
  const commentsRef = collection(firestore, "tasks", taskId, "comments");
  const finalCommentData = { ...commentData, createdAt: serverTimestamp() };
  const docRef = await addDoc(commentsRef, finalCommentData);
  return { id: docRef.id, ...finalCommentData };
}

/**
 * Retrieves all comments for a specific task ordered chronologically.
 * @param {string} taskId
 * @returns {Promise<Object[]>}
 */
export async function getComments(taskId) {
  const commentsRef = collection(firestore, "tasks", taskId, "comments");
  const q = query(commentsRef, orderBy("createdAt", "asc"));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
}

/**
 * Subscribes to real-time updates for a single task document.
 * @param {string} taskId
 * @param {function} callback
 * @returns {function} Unsubscribe function.
 */
export function subscribeToTask(taskId, callback) {
  const docRef = doc(firestore, "tasks", taskId);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
    } else {
      callback(null);
    }
  }, (error) => {
    console.error("Error subscribing to task:", error);
  });
}

/**
 * Fetches all documents from task-labels collection, ordered by name.
 * @returns {Promise<Object[]>}
 */
export async function getAllLabels() {
  const labelsRef = collection(firestore, "task-labels");
  const q = query(labelsRef, orderBy("name", "asc"));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
}

/**
 * Subscribes to real-time updates for the task-labels collection.
 * Used by ALL views (TasksDashboard, TaskDetail) so labels stay in sync
 * across users and sessions without any manual refresh needed.
 * @param {function} callback
 * @returns {function} Unsubscribe function.
 */
export function subscribeToLabels(callback) {
  const labelsRef = collection(firestore, "task-labels");
  const q = query(labelsRef, orderBy("name", "asc"));
  return onSnapshot(q, (querySnapshot) => {
    const labels = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    callback(labels);
  }, (error) => {
    console.error("Error subscribing to labels:", error);
  });
}

/**
 * Creates a new label only if one with the same name does not already exist.
 * Returns the existing label if found, preventing duplicates across sessions/users.
 * Labels are always stored in lowercase.
 * @param {string} name
 * @param {string} color
 * @returns {Promise<Object>}
 */
export async function createLabel(name, color, createdBy = "") {
  const labelsRef = collection(firestore, "task-labels");
  const normalizedName = name.toLowerCase().trim();

  // Return existing label if one with the same normalized name already exists
  const existingSnap = await getDocs(query(labelsRef, where("name", "==", normalizedName)));
  if (!existingSnap.empty) {
    const existingDoc = existingSnap.docs[0];
    const data = existingDoc.data();
    if (!data.createdBy) {
      const creator = createdBy || getAuth().currentUser?.uid || "";
      try {
        await updateDoc(existingDoc.ref, { createdBy: creator });
      } catch (err) {
        console.warn("Failed to set creator on existing label (ignoring due to permissions):", err);
      }
      return { id: existingDoc.id, ...data, createdBy: creator };
    }
    return { id: existingDoc.id, ...data };
  }

  const creator = createdBy || getAuth().currentUser?.uid || "";
  const labelData = { name: normalizedName, color, createdAt: serverTimestamp(), createdBy: creator };
  const docRef = await addDoc(labelsRef, labelData);
  return { id: docRef.id, ...labelData };
}

/**
 * Updates a label's name and/or color. If the name changes, batch-updates all tasks containing the old name.
 * @param {string} labelId
 * @param {Object} updates
 */
export async function updateLabel(labelId, updates) {
  const labelRef = doc(firestore, "task-labels", labelId);
  const oldLabelDoc = await getDoc(labelRef);
  if (!oldLabelDoc.exists()) throw new Error("Label not found");
  const oldName = oldLabelDoc.data().name;
  await updateDoc(labelRef, updates);
  if (updates.name && updates.name !== oldName) {
    const tasksRef = collection(firestore, "tasks");
    const q = query(tasksRef, where("labels", "array-contains", oldName));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const batch = writeBatch(firestore);
      querySnapshot.forEach(docSnap => {
        const taskData = docSnap.data();
        const newLabels = (taskData.labels || []).map(l => l === oldName ? updates.name : l);
        batch.update(docSnap.ref, { labels: newLabels, updatedAt: serverTimestamp() });
      });
      await batch.commit();
    }
  }
}

/**
 * Deletes a label document and batch-removes the label from all tasks.
 * @param {string} labelId
 * @param {string} labelName
 */
export async function deleteLabel(labelId, labelName) {
  const labelRef = doc(firestore, "task-labels", labelId);
  await deleteDoc(labelRef);
  const tasksRef = collection(firestore, "tasks");
  const q = query(tasksRef, where("labels", "array-contains", labelName));
  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    const batch = writeBatch(firestore);
    querySnapshot.forEach(docSnap => {
      const taskData = docSnap.data();
      const newLabels = (taskData.labels || []).filter(l => l !== labelName);
      batch.update(docSnap.ref, { labels: newLabels, updatedAt: serverTimestamp() });
    });
    await batch.commit();
  }
}

/**
 * Fetches tasks that have a specific label applied.
 * @param {string} labelName
 * @returns {Promise<Object[]>}
 */
export async function getTasksByLabel(labelName) {
  const tasksRef = collection(firestore, "tasks");
  const q = query(tasksRef, where("labels", "array-contains", labelName));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(docSnap => ({
    id: docSnap.id,
    labels: [],
    ...docSnap.data()
  }));
}

/**
 * Fetches all user profiles from Firestore for task assignment, ordered by name.
 * @returns {Promise<Array<{uid: string, name: string, email: string, photoURL: string, status: string}>>}
 */
export async function listTaskUsers() {
  const usersRef = collection(firestore, "users");
  const q = query(usersRef, orderBy("name", "asc"));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((docSnap) => ({
    uid: docSnap.id,
    name: docSnap.data().name || docSnap.data().email || docSnap.id,
    email: docSnap.data().email || "",
    photoURL: docSnap.data().photoURL || "",
    status: docSnap.data().status || "Active",
  }));
}

/**
 * Automatically migrates existing task priority values from legacy formats (Priority 1-4, P1-P4, numbers)
 * to standard "Low", "Medium", "High" strings.
 */
export async function migratePriorities() {
  try {
    const querySnapshot = await getDocs(collection(firestore, "tasks"));
    querySnapshot.forEach(async (document) => {
      const data = document.data();
      const oldPriority = data.priority;
      let newPriority = null;

      if (
        oldPriority === "Critical" ||
        oldPriority === "Priority 1" ||
        oldPriority === "P1" ||
        oldPriority === 1 ||
        oldPriority === "1"
      ) {
        newPriority = "High";
      } else if (
        oldPriority === "High" ||
        oldPriority === "Priority 2" ||
        oldPriority === "P2" ||
        oldPriority === 2 ||
        oldPriority === "2"
      ) {
        newPriority = "Medium";
      } else if (
        oldPriority === "Medium" ||
        oldPriority === "Priority 3" ||
        oldPriority === "P3" ||
        oldPriority === 3 ||
        oldPriority === "3"
      ) {
        newPriority = "Low";
      } else if (
        oldPriority === "Low" ||
        oldPriority === "Priority 4" ||
        oldPriority === "P4" ||
        oldPriority === 4 ||
        oldPriority === "4"
      ) {
        newPriority = "Low";
      }

      if (newPriority && newPriority !== oldPriority) {
        const docRef = doc(firestore, "tasks", document.id);
        await updateDoc(docRef, { priority: newPriority });
        console.log(`Migrated task ${data.taskNumber || document.id} priority from "${oldPriority}" to "${newPriority}"`);
      }
    });
  } catch (err) {
    console.error("Task priority migration failed:", err);
  }
}

/**
 * Scans all tasks, gathers all unique labels, and ensures they exist in the
 * task-labels collection. For existing labels without a createdBy field,
 * sets it to Admin@bmspune.org. For new orphan labels, creates them with
 * default color and Admin@bmspune.org as creator.
 */
export async function backfillLabels() {
  try {
    // 1. Fetch all users to resolve email/name to UID mapping
    const usersRef = collection(firestore, "users");
    const usersSnap = await getDocs(usersRef);
    const emailToUidMap = new Map();
    const nameToUidMap = new Map();
    let defaultAdminUid = "";

    usersSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const uid = docSnap.id;
      if (data.email) {
        const email = data.email.toLowerCase().trim();
        emailToUidMap.set(email, uid);
        if (email === "admin@bmspune.org") {
          defaultAdminUid = uid;
        }
      }
      if (data.name) {
        nameToUidMap.set(data.name.toLowerCase().trim(), uid);
      }
    });

    // Fallbacks for defaultAdminUid
    if (!defaultAdminUid) {
      usersSnap.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.isAdmin && !defaultAdminUid) {
          defaultAdminUid = docSnap.id;
        }
      });
    }
    if (!defaultAdminUid && usersSnap.size > 0) {
      defaultAdminUid = usersSnap.docs[0].id;
    }

    // 2. Scan all tasks for label names
    const tasksRef = collection(firestore, "tasks");
    const tasksSnap = await getDocs(tasksRef);

    const uniqueTaskLabels = new Set();
    tasksSnap.forEach((docSnap) => {
      const data = docSnap.data();
      if (Array.isArray(data.labels)) {
        data.labels.forEach((l) => {
          if (l) {
            uniqueTaskLabels.add(l.toLowerCase().trim());
          }
        });
      }
    });

    // 3. Scan all existing task-labels
    const labelsRef = collection(firestore, "task-labels");
    const labelsSnap = await getDocs(labelsRef);
    const existingLabelsMap = new Map();

    labelsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.name) {
        existingLabelsMap.set(data.name.toLowerCase().trim(), docSnap);
      }
    });

    const batch = writeBatch(firestore);
    let batchCount = 0;

    // 4. Migrate existing task-labels from email/name to UID
    for (const [, docSnap] of existingLabelsMap.entries()) {
      const data = docSnap.data();
      const createdBy = data.createdBy;
      
      let targetUid = "";
      if (createdBy) {
        const trimmedCreator = createdBy.trim();
        if (trimmedCreator.includes("@")) {
          targetUid = emailToUidMap.get(trimmedCreator.toLowerCase()) || defaultAdminUid;
        } else if (emailToUidMap.has(trimmedCreator.toLowerCase())) {
          targetUid = emailToUidMap.get(trimmedCreator.toLowerCase());
        } else if (nameToUidMap.has(trimmedCreator.toLowerCase())) {
          targetUid = nameToUidMap.get(trimmedCreator.toLowerCase());
        } else {
          // If it's already a valid UID (exists in users collection as a document ID)
          const isExistingUid = usersSnap.docs.some(doc => doc.id === trimmedCreator);
          if (isExistingUid) {
            targetUid = trimmedCreator;
          } else {
            targetUid = defaultAdminUid;
          }
        }
      } else {
        targetUid = defaultAdminUid;
      }

      if (targetUid && targetUid !== createdBy) {
        batch.update(docSnap.ref, { createdBy: targetUid });
        batchCount++;
      }
    }

    // 5. Backfill any labels that exist in tasks but are missing from task-labels
    for (const labelName of uniqueTaskLabels) {
      if (!existingLabelsMap.has(labelName)) {
        const newLabelRef = doc(collection(firestore, "task-labels"));
        batch.set(newLabelRef, {
          name: labelName,
          color: "#6b7280",
          createdAt: serverTimestamp(),
          createdBy: defaultAdminUid
        });
        batchCount++;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
      console.log(`Successfully backfilled/updated ${batchCount} labels to track user UIDs.`);
    }
  } catch (err) {
    console.error("Error backfilling labels:", err);
  }
}

/**
 * Fetches all tasks and counts how many times each label is assigned.
 * Returns a Map of labelName (lowercase, trimmed) to count.
 * @returns {Promise<Map<string, number>>}
 */
export async function getTaskCountsPerLabel() {
  const tasksRef = collection(firestore, "tasks");
  const querySnapshot = await getDocs(tasksRef);
  const counts = new Map();

  querySnapshot.forEach((docSnap) => {
    const data = docSnap.data();
    if (Array.isArray(data.labels)) {
      data.labels.forEach((label) => {
        if (label) {
          const normalized = label.toLowerCase().trim();
          counts.set(normalized, (counts.get(normalized) || 0) + 1);
        }
      });
    }
  });

  return counts;
}


/**
 * Completes a recurring task by advancing its due date per the recurrence rule,
 * or marks it permanently closed if the task is not recurring.
 *
 * Implements single-entity mutation: no new task rows are created.
 *
 * @param {string} taskId - Firestore document ID
 * @param {string} completionDateStr - ISO "YYYY-MM-DD" of when the task was completed
 * @param {string} userUid - UID of the completing user
 * @param {string} userName - Display name of the completing user
 * @returns {Promise<{advanced: boolean, nextDueDate: string|null, previousDueDate: string|null}>}
 */
export async function completeRecurringTask(taskId, completionDateStr, userUid, userName) {
  const { calculateNextDueDate } = await import("../utils/recurrenceEngine");
  const taskRef = doc(firestore, "tasks", taskId);
  const taskSnap = await getDoc(taskRef);

  if (!taskSnap.exists()) {
    throw new Error(`Task ${taskId} not found`);
  }

  const task = taskSnap.data();
  const recurrence = task.recurrence;
  const previousDueDate = task.dueDate || null;

  if (!recurrence || !recurrence.isRecurring) {
    const commentsRef = collection(firestore, "tasks", taskId, "comments");
    await addDoc(commentsRef, {
      authorUid: userUid || "system",
      authorName: userName || "System",
      isSystem: true,
      type: "state_change",
      text: `Status changed from "${task.status}" to "Closed" by ${userName || "System"}`,
      oldValue: task.status,
      newValue: "Closed",
      createdAt: serverTimestamp(),
    });
    await updateDoc(taskRef, {
      status: "Closed",
      updatedByUid: userUid || "system",
      updatedAt: serverTimestamp(),
    });
    return { advanced: false, nextDueDate: null, previousDueDate };
  }

  // Check if max occurrences reached
  const maxOccurrences = recurrence.maxOccurrences ? parseInt(recurrence.maxOccurrences, 10) : null;
  const currentCount = recurrence.occurrenceCount || 1;

  if (maxOccurrences && currentCount >= maxOccurrences) {
    const commentsRef = collection(firestore, "tasks", taskId, "comments");
    await addDoc(commentsRef, {
      authorUid: userUid || "system",
      authorName: userName || "System",
      isSystem: true,
      type: "recurrence_ended",
      text: `Recurrence completed (${currentCount}/${maxOccurrences} occurrences reached) — task permanently closed by ${userName || "System"}`,
      oldValue: previousDueDate,
      newValue: null,
      createdAt: serverTimestamp(),
    });
    await updateDoc(taskRef, {
      status: "Closed",
      "recurrence.isRecurring": false,
      "recurrence.ended": true,
      "recurrence.occurrenceCount": currentCount,
      updatedByUid: userUid || "system",
      updatedAt: serverTimestamp(),
    });

    return { advanced: false, nextDueDate: null, previousDueDate };
  }

  const nextDueDate = calculateNextDueDate(
    task.dueDate || completionDateStr,
    completionDateStr,
    recurrence
  );

  const commentsRef = collection(firestore, "tasks", taskId, "comments");

  if (nextDueDate) {
    const nextCount = currentCount + 1;
    const progressText = maxOccurrences ? `(occurrence ${currentCount}/${maxOccurrences}) ` : "";
    await addDoc(commentsRef, {
      authorUid: userUid || "system",
      authorName: userName || "System",
      isSystem: true,
      type: "recurrence_advance",
      text: `Recurring task completed ${progressText}by ${userName || "System"} — next due ${nextDueDate}`,
      oldValue: previousDueDate,
      newValue: nextDueDate,
      createdAt: serverTimestamp(),
    });
    await updateDoc(taskRef, {
      dueDate: nextDueDate,
      status: "Open",
      "recurrence.occurrenceCount": nextCount,
      updatedByUid: userUid || "system",
      updatedAt: serverTimestamp(),
    });

    if (recurrence.resetSubtasks) {
      await resetSubtasks(taskId);
    }

    return { advanced: true, nextDueDate, previousDueDate };
  }

  await addDoc(commentsRef, {
    authorUid: userUid || "system",
    authorName: userName || "System",
    isSystem: true,
    type: "recurrence_ended",
    text: `Recurrence ended (past end date) — task permanently closed by ${userName || "System"}`,
    oldValue: previousDueDate,
    newValue: null,
    createdAt: serverTimestamp(),
  });
  await updateDoc(taskRef, {
    status: "Closed",
    "recurrence.isRecurring": false,
    "recurrence.ended": true,
    updatedByUid: userUid || "system",
    updatedAt: serverTimestamp(),
  });

  return { advanced: false, nextDueDate: null, previousDueDate };
}

/**
 * Permanently completes a recurring task, bypassing recurrence logic.
 * The task is marked as Closed and recurrence is disabled.
 *
 * @param {string} taskId - Firestore document ID
 * @param {string} userUid - UID of the completing user
 * @param {string} userName - Display name of the completing user
 * @returns {Promise<void>}
 */
export async function completeTaskForever(taskId, userUid, userName) {
  const taskRef = doc(firestore, "tasks", taskId);
  const taskSnap = await getDoc(taskRef);

  if (!taskSnap.exists()) {
    throw new Error(`Task ${taskId} not found`);
  }

  const task = taskSnap.data();
  const commentsRef = collection(firestore, "tasks", taskId, "comments");

  await addDoc(commentsRef, {
    authorUid: userUid || "system",
    authorName: userName || "System",
    isSystem: true,
    type: "completed_forever",
    text: `Task permanently completed (recurrence stopped) by ${userName || "System"}`,
    oldValue: task.status,
    newValue: "Closed",
    createdAt: serverTimestamp(),
  });

  const updates = {
    status: "Closed",
    updatedByUid: userUid || "system",
    updatedAt: serverTimestamp(),
  };

  if (task.recurrence) {
    updates["recurrence.isRecurring"] = false;
    updates["recurrence.ended"] = true;
  }

  await updateDoc(taskRef, updates);
}

/**
 * Resets all subtasks (children) of a parent task to "Open" status.
 * Queries tasks where parentId matches the given parent task ID.
 *
 * Currently a no-op if no subtasks exist (subtask feature not yet implemented).
 *
 * @param {string} parentTaskId - Firestore document ID of the parent task
 * @returns {Promise<number>} Number of subtasks reset
 */
export async function resetSubtasks(parentTaskId) {
  const tasksRef = collection(firestore, "tasks");
  const q = query(tasksRef, where("parentId", "==", parentTaskId));
  const snapshot = await getDocs(q);

  if (snapshot.empty) return 0;

  const batch = writeBatch(firestore);
  snapshot.forEach((docSnap) => {
    batch.update(docSnap.ref, {
      status: "Open",
      updatedAt: serverTimestamp(),
    });
  });

  await batch.commit();
  return snapshot.size;
}

